import { APP_INITIALIZER, NgModule } from '@angular/core';

import { HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { NgImageSliderModule } from "ng-image-slider";
import { authInterceptorProviders } from './_helpers/auth.interceptor';
import { correlationIdInterceptorProviders } from './_helpers/correlation-id.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { ModalComponent } from './modal/modal.component';
import { ProfileComponent } from './profile/profile.component';
import { RegisterComponent } from './register/register.component';
import { xsrfInterceptorProviders } from './_helpers/xsrf.interceptor';

import { InicioComponent } from './inicio/inicio.component';
import { TramitesEmvsComponent } from './inicio/tramites-emvs/tramites-emvs.component';
import { FooterEmvsComponent } from './layout/footer-emvs/footer-emvs.component';
import { HeaderEmvsComponent } from './layout/header-emvs/header-emvs.component';

import { DatePipe, DecimalPipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { CookieModule } from 'ngx-cookie';
import { NgxMaskModule } from 'ngx-mask-2';
import { DecimaLimitDirective } from './directives/decima-limit.directive';
import { AuthGuard } from './guards/auth.guard';
import { GestionTramitesComponent } from './inicio/gestion-tramites/gestion-tramites.component';

import { ModalModule } from 'ngx-bootstrap/modal';
import { NgPipesModule } from 'ngx-pipes';

import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';

import { FormAvisosComponent } from './avisos/form-avisos/form-avisos.component';
import { EditAvisoDialog, ListadoAvisosComponent } from './avisos/listado-avisos/listado-avisos.component';
import { ModalAvisosComponent } from './avisos/modal-avisos/modal-avisos.component';
import { MaterialModule } from './material/material.module';

import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UrlService } from './_services/urlService';
import { AutoFirmaComponent } from './auto-firma/auto-firma';
import { InfoLegalComponent } from './info-legal/info-legal/info-legal.component';
import { PreguntasEmvsComponent } from './inicio/preguntas-emvs/preguntas-emvs.component';
import { SliderEmvsComponent } from './layout/slider-emvs/slider-emvs.component';
import { ConfirmUpdateDialogComponent } from './modal/confirmar-update/confirm-update-dialog/confirm-update-dialog.component';
import { ConfirmDeleteDialogComponent } from './modal/confirm-delete/confirm-delete-dialog.component';
import { NotificationComponent } from './notification/notification.component';
import { CardDireccionesComponent } from './profile/card-direcciones/card-direcciones.component';


import { PopoverModule } from 'ngx-bootstrap/popover';
import { RaizComponent } from './raiz/raiz.component';
import { TramiteRPSVComponent } from './tipo_tramites/tramite-rpsv/tramite-rpsv.component';

import { ListadoTramitesEmvsComponent } from './listado/listado-tramites-emvs/listado-tramites-emvs.component';

import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { NgxSpinnerModule } from "ngx-spinner";
import { BoardAdminComponent } from './board-admin/board-admin.component';
import { BotonListado } from './boton-listado/boton-listado.component';
import { NavService } from './boton-listado/boton-listado.component.service';
import { ClaveComponent } from './clave/clave.component';
import { ConfirmarCorreoComponent } from './confirmar-correo/confirmar-correo.component';
import { ClientLoaderService } from './core/util/client-loader.service';
import { CustomMatPaginatorIntl } from './core/util/custom-mat-paginator-intl.service';
import { LanguageService } from './core/util/language.service';
import { CustomClientTranslateLoader } from './core/util/translate-client-loader.service';
import { LanguageSwitcherComponent } from './layout/language-switcher/language-switcher.component';
import { CsvViewerComponent } from './csv-viewer/csv-viewer.component';
import { ReloadDirectiveDirective } from './directives/reload-directive.directive';
import { FirmaClaveComponent } from './firma-clave/firma-clave.component';
import { EnvironmentGuard } from './guards/environment.guard';
import { NoLoginGuard } from './guards/noLogin.guard';
import { EditProcedimientosDialog, ListadoProcedimientosComponent } from './listado/listado-procedimientos/listado-procedimientos.component';
import { ConfirmActionDialogComponent } from './modal/confirm-action/confirm-action-dialog.component';
import { ConfirmOtpDialogComponent } from './modal/confirm-otp/confirm-otp-dialog.component';
import { ComunicacionesUsuarioComponent } from './notifica/comunicaciones-usuario/comunicaciones-usuario.component';
import { ComunicacionDetallesComponent } from './notifica/modal-detalles/comunicacion-detalles.component';
import { NotificacionesAceptadasUsuarioComponent } from './notifica/notificaciones-aceptadas-usuario/notificaciones-aceptadas-usuario.component';
import { NotificacionesAutorizacionesComponent } from './notifica/notificaciones-autorizaciones/notificaciones-autorizaciones.component';
import { NotificacionesUsuarioComponent } from './notifica/notificaciones-usuario/notificaciones-usuario.component';
import { Anexo3ParticipacionConcursoComponent } from './tipo_tramites/anexo3-participacion-concurso/anexo3-participacion-concurso.component';
import { TramiteInstanciaGeneralComponent } from './tipo_tramites/tramite-instancia-general/tramite-instancia-general.component';
import { TramiteSPVComponent } from './tipo_tramites/tramite-spv/tramite-spv.component';
import { TramiteDynamicComponent } from './tramite-dynamic/tramite-dynamic.component';

export function createTranslateLoader(http: HttpClient, clientLoader: ClientLoaderService) {
  return new CustomClientTranslateLoader(http, clientLoader);
}

export function initApp(clientLoader: ClientLoaderService, languageService: LanguageService) {
  return () => clientLoader.loadClient().then(() => languageService.init());
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    ProfileComponent,
    BoardAdminComponent,
    ModalComponent,
    InicioComponent,
    TramitesEmvsComponent,
    DecimaLimitDirective,
    GestionTramitesComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ListadoAvisosComponent,
    FormAvisosComponent,
    ModalAvisosComponent,
    EditAvisoDialog,
    EditAvisoDialog,
    ConfirmUpdateDialogComponent,
    ConfirmDeleteDialogComponent,
    InfoLegalComponent,
    CardDireccionesComponent,
    NotificationComponent,
    AutoFirmaComponent,
    HeaderEmvsComponent,
    FooterEmvsComponent,
    SliderEmvsComponent,
    PreguntasEmvsComponent,
    RaizComponent,
    TramiteRPSVComponent,
    ClaveComponent,
    ReloadDirectiveDirective,
    ListadoTramitesEmvsComponent,
    ListadoProcedimientosComponent,
    EditProcedimientosDialog,
    ConfirmarCorreoComponent,
    NotificacionesUsuarioComponent,
    ComunicacionesUsuarioComponent,
    NotificacionesAutorizacionesComponent,
    ComunicacionDetallesComponent,
    NotificacionesAceptadasUsuarioComponent,
    ConfirmOtpDialogComponent,
    FirmaClaveComponent,
    TramiteInstanciaGeneralComponent,
    Anexo3ParticipacionConcursoComponent,
    TramiteDynamicComponent,
    CsvViewerComponent,
    TramiteSPVComponent,
    ConfirmActionDialogComponent,
    LanguageSwitcherComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'XSRF-TOKEN',
      headerName: 'X-XSRF-TOKEN',
    }),
    NgImageSliderModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    TabsModule.forRoot(),
    CookieModule.withOptions(),
    NgxMaskModule.forRoot({ dropSpecialCharacters: false }),
    SimpleNotificationsModule.forRoot(),
    NgPipesModule,
    MaterialModule,
    ModalModule,
    PopoverModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    BotonListado,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, ClientLoaderService],
      },
    }),
  ],
  providers: [correlationIdInterceptorProviders, authInterceptorProviders, xsrfInterceptorProviders,
    // errorInterceptorProviders,
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    DatePipe,
    DecimalPipe,
    AuthGuard,
    EnvironmentGuard,
    NoLoginGuard,
    MatTableDataSource,
    FormGroupDirective,
    MatDatepickerModule,
    NavService,
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [ClientLoaderService, LanguageService],
      multi: true,
    },
  ],
  exports: [
    BotonListado
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private urlService: UrlService) { }

}